import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { BookOpen, ExternalLink, ChevronLeft, Calendar, FileText, CheckCircle2 } from 'lucide-react';

import { Breadcrumb } from '../../components/common/Breadcrumb';

export const PublicationDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: book, isLoading, error } = useQuery({
    queryKey: ['public-publication-detail', slug],
    queryFn: () => api.get(`/public/publications/${slug}`).then((res) => res.data.data),
    enabled: !!slug,
  });

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-xs font-mono">Loading publication...</div>;
  }

  if (error || !book) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-serif font-bold text-slate-900">Publication Not Found</h2>
        <Link to="/publications" className="text-xs font-bold text-brand-700 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          items={[
            { label: 'Publications', href: '/publications' },
            { label: book.title },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Cover image column */}
        <div className="md:col-span-5 space-y-6">
          <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-book border border-slate-200 bg-slate-50 flex items-center justify-center p-6">
            <img src={book.coverImage} alt={book.title} className="max-h-full max-w-full object-contain drop-shadow-xl" />
          </div>

          {book.bookstoreUrl && (
            <a
              href={book.bookstoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs shadow-md transition-colors"
            >
              <span>Purchase at For The Truth Bookstore</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        {/* Details column */}
        <div className="md:col-span-7 space-y-6">
          <div className="space-y-2">
            <span className="text-xs font-mono uppercase tracking-widest text-brand-700 font-bold">
              {book.category}
            </span>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-editorial-dark leading-tight">
              {book.title}
            </h1>
            <p className="text-base text-slate-600 font-medium">
              By <span className="font-bold text-slate-900">{book.author}</span>
            </p>
          </div>

          {/* Metadata Chips */}
          <div className="grid grid-cols-2 gap-3 p-4 rounded-xl bg-white border border-editorial-border text-xs font-mono">
            <div>
              <span className="text-slate-400 block text-[10px] uppercase">Format</span>
              <span className="font-bold text-slate-800">{book.format}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase">Publisher</span>
              <span className="font-bold text-slate-800">{book.publisher}</span>
            </div>
            {book.isbn && (
              <div>
                <span className="text-slate-400 block text-[10px] uppercase">ISBN</span>
                <span className="font-bold text-slate-800">{book.isbn}</span>
              </div>
            )}
            <div>
              <span className="text-slate-400 block text-[10px] uppercase">Publication Date</span>
              <span className="font-bold text-slate-800">
                {new Date(book.publicationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
              </span>
            </div>
          </div>

          {/* Synopsis */}
          <div className="space-y-3">
            <h3 className="font-serif text-lg font-bold text-slate-900">About this Volume</h3>
            <p className="text-sm text-slate-600 leading-relaxed font-sans">
              {book.description}
            </p>
          </div>

          {/* Excerpt */}
          {book.excerpt && (
            <div className="p-6 rounded-xl bg-editorial-parchment border-l-4 border-brand-500 space-y-2">
              <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-800 block">
                Selected Excerpt
              </span>
              <blockquote className="font-serif italic text-sm text-slate-800 leading-relaxed">
                &ldquo;{book.excerpt}&rdquo;
              </blockquote>
            </div>
          )}

        </div>
      </div>

    </div>
  );
};
